import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import PhoneActions from "@/components/PhoneActions";
import DispatchInvoiceSection from "@/components/DispatchInvoiceSection";
import { Button } from "@/components/ui/button";
import { MapPinArea } from "@phosphor-icons/react";
import { fmtDate, fmtDateTime } from "@/lib/fmt";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

function extractFirstPhone(text) {
  if (!text) return null;
  const m = text.match(/(\+?\d[\d\s.\-]{7,18}\d)/);
  return m ? m[1].trim() : null;
}

const FR_MAP = { er: "En route", sp: "Sur place", pec: "Client pris en charge", dep: "Course terminée" };

function Field({ label, value }) {
  if (!value && value !== 0) return null;
  return <div><p className="text-xs text-zinc-500 uppercase tracking-wider mb-0.5">{label}</p><p className="text-sm text-white whitespace-pre-wrap">{value}</p></div>;
}

export default function RideDetailModal({ open, onClose, ride, drivers, onUpdated }) {
  const { user } = useAuth();
  if (!ride) return null;
  const assignedDriver = drivers?.find(d => d.id === ride.assigned_driver);
  const interestedNames = (ride.interested_drivers || []).map(id => drivers?.find(d => d.id === id)?.name || "Unknown").join(", ");
  const history = ride.status_history || [];
  const isAdmin = user?.role === "admin";
  const canUpdateStatus = isAdmin && ride.assigned_driver && !["cancelled","archived","completed"].includes(ride.status);

  const setStatus = async (status) => {
    try {
      await api.put(`/rides/${ride.id}/admin-status`, { status });
      toast.success("Statut mis à jour");
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg flex items-center gap-2">{ride.route_label}
            <span className={`text-xs px-1.5 py-0.5 rounded border ml-1 ${ride.ride_type === "van" ? "border-sky-500/40 text-sky-400 bg-sky-500/10" : "border-zinc-600 text-zinc-400 bg-zinc-800"}`}>{(ride.ride_type || "eco").toUpperCase()}</span>
          </DialogTitle>
          <DialogDescription className="text-zinc-500">{fmtDate(ride.pickup_date)} at {ride.pickup_time}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-3"><StatusBadge status={ride.status} /><span className="text-amber-500 font-semibold text-lg">{ride.driver_fare} EUR</span></div>

          {canUpdateStatus && (
            <div data-testid="admin-status-block" className="bg-[#0e0e0e] border border-zinc-800 rounded-md p-3">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Mettre à jour le statut</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { k: "er", l: "En route", tone: "bg-sky-500/15 text-sky-300 border-sky-500/30 hover:bg-sky-500/25" },
                  { k: "sp", l: "Sur place", tone: "bg-amber-500/15 text-amber-300 border-amber-500/30 hover:bg-amber-500/25" },
                  { k: "pec", l: "Client pris en charge", tone: "bg-orange-500/15 text-orange-300 border-orange-500/30 hover:bg-orange-500/25" },
                  { k: "dep", l: "Course terminée", tone: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25" },
                ].map(s => (
                  <button key={s.k} data-testid={`admin-status-${s.k}`} onClick={() => setStatus(s.k)} className={`h-9 rounded-md border text-xs font-medium transition-colors ${s.tone} ${ride.status === s.k ? "ring-2 ring-offset-2 ring-offset-[#121212] ring-white/30" : ""}`}>
                    {s.l}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3"><Field label="Passagers" value={ride.passenger_count} /><Field label="Bagages" value={ride.luggage_count} /></div>

          {/* Phase 30 — admin-only financial breakdown */}
          {ride.finance && (
            <div data-testid="admin-finance-block" className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-xs text-amber-300 uppercase tracking-wider font-semibold">Détail financier</p>
                {ride.finance.is_pab && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 uppercase">PAB</span>}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><p className="text-[10px] uppercase text-zinc-500">Prix client</p><p data-testid="finance-client-price" className="text-base font-bold text-white">{Number(ride.finance.client_price).toFixed(2)} EUR</p></div>
                <div><p className="text-[10px] uppercase text-zinc-500">Chauffeur</p><p data-testid="finance-driver-amount" className="text-base font-bold text-sky-300">{Number(ride.finance.driver_amount).toFixed(2)} EUR</p></div>
                <div><p className="text-[10px] uppercase text-zinc-500">Commission</p><p data-testid="finance-commission" className="text-base font-bold text-amber-300">{Number(ride.finance.commission).toFixed(2)} EUR</p></div>
              </div>
            </div>
          )}

          {ride.sensitive_hidden ? (
            <div data-testid="ride-sensitive-locked" className="rounded border border-amber-500/30 bg-amber-500/5 p-3">
              <p className="text-xs text-amber-200 leading-snug">
                Informations client disponibles 24h avant la prise en charge.
              </p>
            </div>
          ) : (
            <>
              {ride.info_block && <div><p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Informations client</p><div className="bg-[#1A1A1A] border border-zinc-800 rounded p-3 text-sm text-zinc-300 whitespace-pre-wrap max-h-48 overflow-y-auto">{ride.info_block}</div>{extractFirstPhone(ride.info_block) && <div className="pt-2"><PhoneActions phone={extractFirstPhone(ride.info_block)} size="xs" withSms /></div>}</div>}
              {ride.notes && <Field label="Notes" value={ride.notes} />}
            </>
          )}
          {interestedNames && <Field label="Chauffeurs intéressés" value={interestedNames} />}
          {assignedDriver && <Field label="Chauffeur attribué" value={`${assignedDriver.name} - ${assignedDriver.phone || "Pas de téléphone"}`} />}
          {ride.no_show && (
            <div className="border border-amber-500/40 bg-amber-500/5 rounded-lg p-3 space-y-2" data-testid="no-show-block">
              <p className="text-xs text-amber-400 uppercase tracking-wider font-semibold">No-show signalé</p>
              <p className="text-[11px] text-zinc-500">{ride.no_show.reported_at ? fmtDateTime(ride.no_show.reported_at) : ""}</p>
              {ride.no_show.photo_data_url && (
                <a data-testid="no-show-photo-link" href={ride.no_show.photo_data_url} download={`no-show-${ride.id}.jpg`} className="block">
                  <img src={ride.no_show.photo_data_url} alt="No-show" className="w-full max-h-64 object-cover rounded border border-zinc-800 hover:opacity-90" />
                  <p className="text-[11px] text-amber-400 underline mt-1">Télécharger la photo</p>
                </a>
              )}
              {ride.no_show.reason && <p className="text-sm text-zinc-200 whitespace-pre-wrap">{ride.no_show.reason}</p>}
              {ride.no_show.address && <p className="text-xs text-zinc-500 flex items-center gap-1"><MapPinArea size={12} />{ride.no_show.address}</p>}
              {ride.no_show.lat && !ride.no_show.address && (
                <a href={`https://maps.google.com/?q=${ride.no_show.lat},${ride.no_show.lng}`} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-400 underline">
                  {ride.no_show.lat.toFixed(4)}, {ride.no_show.lng.toFixed(4)} (voir sur Maps)
                </a>
              )}
            </div>
          )}
          {isAdmin && !["cancelled", "archived", "available", "new"].includes(ride.status) && (
            <DispatchInvoiceSection ride={ride} onInvoiceChanged={onUpdated} />
          )}
          {history.length > 0 && (
            <div>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Historique du statut</p>
              <div className="space-y-2">
                {history.map((h, i) => {
                  const ts = h.timestamp ? fmtDateTime(h.timestamp) : "";
                  return (
                    <div key={i} className="bg-[#1A1A1A] border border-zinc-800 rounded p-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-white">{FR_MAP[h.status] || h.status.toUpperCase()}</span>
                        <span className="text-xs text-zinc-500">{ts}</span>
                      </div>
                      {h.address && <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1"><MapPinArea size={12} />{h.address}</p>}
                      {h.lat && !h.address && <p className="text-xs text-zinc-600 mt-1">{h.lat.toFixed(4)}, {h.lng.toFixed(4)}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
